"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zPGBigInt = void 0;
exports.getYesOrNo = getYesOrNo;
exports.makePlural = makePlural;
exports.appendOrdinalIndicator = appendOrdinalIndicator;
exports.isNonEmpty = isNonEmpty;
exports.compactMap = compactMap;
exports.formatCommaSeparatedList = formatCommaSeparatedList;
exports.formatNumberOneDigit = formatNumberOneDigit;
exports.randomizeByPercent = randomizeByPercent;
exports.isPGBigInt = isPGBigInt;
exports.sleep = sleep;
exports.insecureHash = insecureHash;
exports.keyByPartial = keyByPartial;
exports.groupByPartial = groupByPartial;
exports.sumObjectNumericPartial = sumObjectNumericPartial;
exports.pick = pick;
exports.getManualPromise = getManualPromise;
exports.hasOwnProperty = hasOwnProperty;
exports.isInstance = isInstance;
exports.isNonEmptyString = isNonEmptyString;
exports.toLowerCase = toLowerCase;
exports.convertTextToHTML = convertTextToHTML;
exports.getInitials = getInitials;
exports.computeRatio = computeRatio;
const zod_1 = require("zod");
const lodash_1 = require("lodash");
const html_entities_1 = require("html-entities");
function getYesOrNo(property) {
    return property === true ? "Yes" : "No";
}
function makePlural(count) {
    return count === 1 ? "" : "s";
}
function appendOrdinalIndicator(value) {
    const stringified = Math.round(value).toString();
    const lastChar = stringified.at(-1);
    const secondToLastChar = stringified.at(-2);
    const suffix = secondToLastChar === "1" ? "th"
        : lastChar === "1" ? "st"
            : lastChar === "2" ? "nd"
                : lastChar === "3" ? "rd"
                    : "th";
    return `${stringified}${suffix}`;
}
function isNonEmpty(array) {
    return array.length > 0;
}
function compactMap(array, mapper) {
    return array.map(mapper).filter(elem => elem !== null);
}
function formatCommaSeparatedList(items) {
    switch (items.length) {
        case 1:
            return items[0];
        case 2:
            return items.join(" and ");
        default:
            const incompleteList = items.slice(0, items.length - 1).join(", ");
            return incompleteList + `, and ${items[items.length - 1]}`;
    }
}
const numberFormatSingleDigit = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
});
function formatNumberOneDigit(value) {
    return numberFormatSingleDigit.format(value);
}
// Randomizes value by plus or minus percent
function randomizeByPercent(value, percent = 10) {
    percent = percent / 100;
    const base = value - value * percent;
    const addOn = value * percent * 2 * Math.random();
    return Math.floor(base + addOn);
}
const zCoercedPositiveBigInt = zod_1.z.coerce.bigint().positive();
// N.B. This validator does NOT check that the bigint is within postgres's
// boundaries. A JavaScript bigint can be much bigger than PG's.
// https://www.postgresql.org/docs/15/datatype-numeric.html
// This is taken as a shortcut to not add further complexity to the schema,
// but if desired, adding a simple number comparator to the .refine() may
// be sufficient. See test cases to confirm the new behavior.
exports.zPGBigInt = zod_1.z
    .string()
    .refine(str => {
    // We wrap the zBigInt.safeParse() because the coercing throws an error for
    // invalid bigints.
    let bigIntParseSuccess = false;
    try {
        bigIntParseSuccess = zCoercedPositiveBigInt.safeParse(str).success;
    }
    catch {
        return false;
    }
    return str.length !== 0 && !str.startsWith("0") && bigIntParseSuccess;
})
    .brand();
function isPGBigInt(x) {
    return exports.zPGBigInt.safeParse(x).success;
}
async function sleep(ms) {
    return await new Promise(resolve => setTimeout(resolve, ms));
}
function insecureHash(str) {
    return btoa(encodeURIComponent(str)).slice(-16);
}
// lodash's keyBy returns a Record<string, T>, so for any string you index it
// with, you'll get back a T. This is often not a good type, since there's
// a chance the key isn't in the map. Hence, we introduce this safer variant,
// which returns a Partial<Record<string, T>>.
function keyByPartial(array, callback) {
    return (0, lodash_1.keyBy)(array, callback);
}
// Similar to keyByPartial. lodash groupBy returns a Record<string, T>, so we
// introduce a safer variant returning Partial<Record<string, T>>.
function groupByPartial(array, iteratee) {
    return (0, lodash_1.groupBy)(array, iteratee);
}
function sumObjectNumericPartial(values, fields) {
    const sums = Object.fromEntries(fields.map(f => [f, 0]));
    const fieldsSet = new Set(fields);
    for (const field of fieldsSet) {
        for (const stat of values) {
            sums[field] += Number(stat[field] ?? 0);
        }
    }
    return sums;
}
// lodash's pick function allows you to give it keys which are not on the
// underlying object. This variant prevents that.
function pick(obj, ...keys) {
    return (0, lodash_1.pick)(obj, ...keys);
}
function getManualPromise() {
    // We have to assign this or else TS complains.
    let resolve = null;
    const promise = new Promise(r => (resolve = r));
    return [promise, resolve];
}
function hasOwnProperty(obj, prop) {
    return Object.prototype.hasOwnProperty.call(obj, prop);
}
// 2019 comment:
// This file was created to make it easier to mock checking whether or not an
// adapter is an instance of a FieldSpecAdapter. Instead of creating actual
// adapters in our test files, we can pass in stubs and mock this function.
//
// 2024 update:
// But it's also useful for checking instanceof for generic class constructors.
function isInstance(value, 
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
constructor) {
    return value instanceof constructor;
}
/**
 * Checks if the provided parameter is a non-`null`, non-`undefined` string type
 * with at least one non-whitespace character.
 */
function isNonEmptyString(str) {
    return typeof str === "string" && str.trim().length > 0;
}
/**
 * Wraps the toLowerCase() string instance method with an explicit cast to
 * {@link Lowercase<string>}
 */
function toLowerCase(str) {
    return str.toLowerCase();
}
function convertTextToHTML(str) {
    // First replace all Windows-style newlines with a line break, and then
    // replace individual newlines with their own break.
    return (0, html_entities_1.encode)(str, { mode: "specialChars", level: "html5" })
        .replaceAll("\r\n", "<br>")
        .replaceAll(/[\r\n]/g, "<br>");
}
function getInitials(username) {
    if (!username) {
        return "?";
    }
    const fullInitials = username.split(/[\s_]+/iu).filter(a => a !== "");
    // This is how we normalize unicode code points in characters.
    // https://dev.to/acanimal/how-to-slice-or-get-symbols-from-a-unicode-string-with-emojis-in-javascript-lets-learn-how-javascript-represent-strings-h3a
    // Otherwise, characters like emoji are split across code points, causing
    // orphaned unicode characters.
    const fullInitialsNormalized = fullInitials.map(f => [...f.toUpperCase()]);
    if (fullInitialsNormalized.length < 2)
        return fullInitialsNormalized[0][0];
    return (fullInitialsNormalized[0][0] +
        fullInitialsNormalized[fullInitialsNormalized.length - 1][0]);
}
function computeRatio(numerator, denominator) {
    if (denominator === 0) {
        return 0;
    }
    return numerator / denominator;
}
//# sourceMappingURL=generic.helpers.js.map